import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Eye, ListChecks, Pencil, Plus, Trash2, X } from "lucide-react";
import { adminApi, errText } from "./adminApi";

const BLANK = { name: "", description: "", published: true };

export default function AdminTopics({ onManageQuestions }) {
  const [topics, setTopics] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/admin/test/topics");
    setTopics(data.topics);
  }, []);
  useEffect(() => { load().catch(() => setError("Could not load topics. Click Refresh or try again.")); }, [load]);

  const openEditor = (t) => {
    setEditing(t?.topic_id || "new");
    setForm(t ? { name: t.name, description: t.description, published: t.published } : BLANK);
    setError("");
  };

  const save = async () => {
    setError("");
    try {
      if (editing === "new") await adminApi.post("/admin/test/topics", form);
      else await adminApi.put(`/admin/test/topics/${editing}`, form);
      setNotice("Topic saved.");
      setEditing(null);
      await load();
    } catch (err) { setError(errText(err)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete topic "${t.name}" permanently?`)) return;
    setError("");
    try { await adminApi.delete(`/admin/test/topics/${t.topic_id}`); await load(); }
    catch (err) { setError(errText(err)); }
  };

  const move = async (id, direction) => { await adminApi.post(`/admin/test/topics/${id}/move`, { direction }); await load(); };

  const openPreview = async (t) => {
    const { data } = await adminApi.get("/admin/test/questions", { params: { topic_id: t.topic_id } });
    const active = data.questions.filter((q) => q.active);
    const picked = [...active].sort(() => Math.random() - 0.5).slice(0, 5);
    setPreview({ topic: t, questions: picked, activeCount: active.length });
  };

  return (
    <div data-testid="admin-topics">
      <h4 style={{ marginBottom: 4 }}>Physics Topic Management</h4>
      <span className="lead-sub">Students pick one published topic per test. A topic's test goes live once it has 5 or more active questions.</span>
      {error && <p className="admin-error" data-testid="topic-error" style={{ marginTop: 12 }}>{error}</p>}
      {notice && <p className="tp-success-box" style={{ background: "#123a2e", border: "1px solid #12b76a", color: "#4ade80" }}>{notice}</p>}
      <div className="admin-row" style={{ margin: "14px 0" }}>
        <button className="admin-btn" onClick={() => openEditor(null)} data-testid="topic-add"><Plus size={14} /> Add topic</button>
      </div>
      {editing && <div className="admin-card" data-testid="topic-editor">
        <div className="admin-row" style={{ justifyContent: "space-between" }}><h4>{editing === "new" ? "New topic" : "Edit topic"}</h4><button className="admin-btn" onClick={() => setEditing(null)}><X size={14} /></button></div>
        {error && <p className="admin-error">{error}</p>}
        <label className="admin-label">TOPIC NAME<input className="admin-input" value={form.name} onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))} placeholder="e.g. Current Electricity" data-testid="topic-name" /></label>
        <label className="admin-label">SHORT DESCRIPTION (OPTIONAL)<input className="admin-input" value={form.description} onChange={(e) => setForm((c) => ({ ...c, description: e.target.value }))} placeholder="One line students see on the topic card" data-testid="topic-desc" /></label>
        <label className="check-row"><input type="checkbox" checked={form.published} onChange={(e) => setForm((c) => ({ ...c, published: e.target.checked }))} data-testid="topic-published" /> Published (visible to students)</label>
        <button className="admin-btn" onClick={save} data-testid="topic-save">Save topic</button>
      </div>}
      <div className="table-scroll"><table className="leads-table" data-testid="topics-table">
        <thead><tr><th>TOPIC</th><th>TOTAL QUESTIONS</th><th>PUBLISHED QUESTIONS</th><th>TEST AVAILABLE</th><th>STATUS</th><th>ACTIONS</th></tr></thead>
        <tbody>{topics.map((t, i) => (
          <tr key={t.topic_id} data-testid={`topic-row-${i}`}>
            <td><div className="lead-name">{t.name}</div><div className="lead-sub">{t.description || "—"}</div></td>
            <td data-testid={`topic-total-${i}`}>{t.total_questions}</td>
            <td data-testid={`topic-active-${i}`}>{t.active_questions}</td>
            <td><span className={t.test_available ? "status-pill approved" : "status-pill locked"} data-testid={`topic-available-${i}`}>{t.test_available ? "Yes" : "No"}</span></td>
            <td><span className={t.published ? "status-pill review" : "status-pill locked"}>{t.published ? "PUBLISHED" : "HIDDEN"}</span></td>
            <td><div className="admin-row" style={{ gap: 6 }}>
              <button className="admin-btn" onClick={() => move(t.topic_id, "up")} disabled={i === 0} data-testid={`topic-up-${i}`}><ArrowUp size={13} /></button>
              <button className="admin-btn" onClick={() => move(t.topic_id, "down")} disabled={i === topics.length - 1} data-testid={`topic-down-${i}`}><ArrowDown size={13} /></button>
              <button className="admin-btn" onClick={() => openEditor(t)} data-testid={`topic-edit-${i}`}><Pencil size={13} /></button>
              <button className="admin-btn" onClick={() => openPreview(t)} data-testid={`topic-preview-${i}`}><Eye size={13} /></button>
              <button className="admin-btn" onClick={() => onManageQuestions(t.topic_id)} data-testid={`topic-manage-${i}`}><ListChecks size={13} /></button>
              <button className="admin-btn" onClick={() => remove(t)} data-testid={`topic-delete-${i}`}><Trash2 size={13} /></button>
            </div></td>
          </tr>
        ))}</tbody>
      </table></div>
      {topics.length === 0 && <div className="admin-empty">No topics yet. Create your first Physics topic, then add at least 5 questions to it.</div>}
      {preview && <div className="tp-modal-backdrop" data-testid="topic-preview-modal" onClick={() => setPreview(null)}><div className="tp-modal" style={{ maxWidth: 640, textAlign: "left" }} onClick={(e) => e.stopPropagation()}>
        <div className="admin-row" style={{ justifyContent: "space-between" }}><h4>Test preview — {preview.topic.name}</h4><button className="admin-btn" onClick={() => setPreview(null)} data-testid="topic-preview-close"><X size={14} /></button></div>
        <p className="lead-sub">{preview.activeCount} active questions in pool — a student gets 5 random ones. Sample draw:</p>
        {preview.questions.length < 5 && <p className="admin-error">Not enough active questions for a test yet (need 5).</p>}
        {preview.questions.map((q, i) => (
          <div key={q.question_id} style={{ borderTop: "1px solid #1e2c44", padding: "10px 0" }} data-testid={`preview-q-${i}`}>
            <p style={{ fontSize: 13, marginBottom: 6 }}><b>Q{i + 1}.</b> {q.text}</p>
            {["A", "B", "C", "D"].map((k) => <p key={k} style={{ fontSize: 12, color: k === q.correct ? "#4ade80" : "#9db2cc", margin: "2px 0" }}>{k}. {q.options[k]}{k === q.correct ? " ✓" : ""}</p>)}
          </div>
        ))}
      </div></div>}
    </div>
  );
}
